import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import MapView from "@/components/MapView";
import usePageTitle from "@/lib/usePageTitle";
import { SignOut, MapPin, Phone, Check, Clock, NavigationArrow, CheckCircle, ListBullets, MapTrifold, X, CaretUp, CaretDown, Brain } from "@phosphor-icons/react";
import { Info, PhotoSlot, BinScanSlot, SkipSlot, HubConfirmSlot } from "./DriverSlots";
export default function DriverView() {
  const { user, logout, loading } = useAuth();
  const [today, setToday] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [view, setView] = useState("list"); // list | map
  const [active, setActive] = useState(null);
  const [learnStatus, setLearnStatus] = useState(null); // null | 'saving' | 'saved'
  const gpsBufferRef = React.useRef([]);
  const gpsWatchRef = React.useRef(null);
  usePageTitle("Fahrer-Tour", tenant?.name);

  useEffect(() => {
    if (!user) return;
    refresh();
    api.get("/tenants/me").then((r) => setTenant(r.data)).catch(() => {});
  }, [user]);

  // ==== GPS-Track (kontinuierlich, batched Flush alle 30 s) ====
  useEffect(() => {
    if (!user || user.role !== "tenant_driver") return;
    if (!navigator.geolocation || !navigator.geolocation.watchPosition) return;
    const startWatch = () => {
      try {
        gpsWatchRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            gpsBufferRef.current.push({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              ts: new Date().toISOString(),
              kind: "track",
            });
          },
          () => {}, // silent
          { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
        );
      } catch (e) { console.info("[DriverView] Geolocation nicht verfügbar", e?.message || e); }
    };
    const flush = async () => {
      const buf = gpsBufferRef.current;
      if (!buf.length) return;
      const points = buf.splice(0, buf.length);
      try { await api.post("/driver/gps-track", { points }); }
      catch (e) { /* push back on failure */ gpsBufferRef.current.unshift(...points.slice(-20)); }
    };
    startWatch();
    const t = setInterval(flush, 30000);
    return () => {
      if (gpsWatchRef.current && navigator.geolocation) navigator.geolocation.clearWatch(gpsWatchRef.current);
      clearInterval(t);
      flush();
    };
  }, [user]);

  const trackJobEvent = async (jobId, kind) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await api.post("/driver/gps-track", {
          points: [{ lat: pos.coords.latitude, lng: pos.coords.longitude, ts: new Date().toISOString(), job_id: jobId, kind }],
        });
      } catch (e) { console.warn("[DriverView] request failed", e); }
    }, () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 8000 });
  };

  const refresh = async () => {
    try { const r = await api.get("/driver/today"); setToday(r.data); } catch (e) { console.error(e); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const setStatus = async (jobId, status) => {
    try {
      await api.patch(`/jobs/${jobId}`, { status });
      if (status === "in_progress") trackJobEvent(jobId, "job_start");
      if (status === "done") trackJobEvent(jobId, "job_done");
      refresh();
    } catch (e) { alert(e.message); }
  };

  const moveJob = async (jobId, direction) => {
    const list = (today?.jobs || []).filter(j => j.status !== "done" && j.status !== "skipped");
    const idx = list.findIndex(j => j.job_id === jobId);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    const [item] = reordered.splice(idx, 1);
    reordered.splice(target, 0, item);
    const doneJobs = (today?.jobs || []).filter(j => j.status === "done" || j.status === "skipped");
    const newFullOrder = [...doneJobs, ...reordered];
    try {
      await api.patch("/driver/tour-order", { job_ids: newFullOrder.map(j => j.job_id) });
      refresh();
    } catch (e) { alert("Reihenfolge konnte nicht gespeichert werden"); }
  };

  const completeTour = async () => {
    setLearnStatus("saving");
    try {
      const r = await api.post("/driver/complete-tour", {});
      setLearnStatus("saved");
      setTimeout(() => setLearnStatus(null), 4000);
    } catch (e) {
      setLearnStatus(null);
      alert(e.response?.data?.detail || "Konnte Reihenfolge nicht speichern");
    }
  };

  const uploadPhoto = async (jobId, kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/photo?kind=${kind}`, {
        method: "POST", body: fd, credentials: "include",
        headers: localStorage.getItem("tonneva_token") && localStorage.getItem("tonneva_token") !== "__cookie__"
          ? { Authorization: `Bearer ${localStorage.getItem("tonneva_token")}` } : {},
      });
      if (!r.ok) throw new Error(`Upload fehlgeschlagen (${r.status})`);
      refresh();
    } catch (e) { alert(e.message); }
  };

  const jobs = today?.jobs || [];
  const stops = jobs
    .filter((j) => j.customer_address?.lat && j.customer_address?.lng)
    .map((j) => ({
      lng: j.customer_address.lng,
      lat: j.customer_address.lat,
      label: `${j.customer_name} — ${j.customer_address?.street || ""}`,
    }));
  const done = jobs.filter((j) => j.status === "done").length;
  const total = jobs.length;
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-screen" style={{ background: "#0e1a23", color: "white" }}>
      {/* Mobile-first header */}
      <header className="sticky top-0 z-30" style={{ background: "rgba(14,26,35,0.95)", backdropFilter: "blur(8px)" }}>
        <div className="px-4 h-14 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src="/brand/tonneva-logo.png" alt="" className="w-7 h-7" />
            <div>
              <div className="font-heading font-bold tracking-tight text-sm">{tenant?.name || "Driver"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--tonneva-secondary)" }}>Fahrer-App</div>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs" data-testid="driver-logout">
            <SignOut size={14} /> {user.name?.split(" ")[0]}
          </button>
        </div>
        {/* Progress bar */}
        {total > 0 && (
          <div className="px-4 py-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="text-white/70">Heute · {done}/{total} erledigt</span>
              <span className="font-mono font-bold">{progress}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="driver-progress" />
            </div>
          </div>
        )}
      </header>

      {/* Mode switch */}
      <div className="flex gap-2 px-4 pt-4 pb-2 max-w-md mx-auto">
        <button onClick={() => setView("list")} className={`flex-1 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 transition ${view === "list" ? "" : "bg-white/5 text-white/60"}`} style={view === "list" ? { background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" } : {}} data-testid="driver-list-btn">
          <ListBullets size={14} weight="bold" /> Liste
        </button>
        <button onClick={() => setView("map")} className={`flex-1 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 transition ${view === "map" ? "" : "bg-white/5 text-white/60"}`} style={view === "map" ? { background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" } : {}} data-testid="driver-map-btn">
          <MapTrifold size={14} weight="bold" /> Karte
        </button>
      </div>

      <main className="max-w-md mx-auto px-4 pb-24">
        {jobs.length === 0 ? (
          <div className="bg-white/5 rounded-2xl border border-white/10 p-10 text-center mt-6">
            <CheckCircle size={48} weight="duotone" className="mx-auto mb-3" style={{ color: "var(--tonneva-secondary)" }} />
            <h2 className="font-heading text-xl font-bold mb-2">Keine Touren heute</h2>
            <p className="text-sm text-white/60">Genießen Sie Ihren Tag oder schauen Sie morgen wieder rein.</p>
          </div>
        ) : view === "map" ? (
          <div className="bg-white/5 rounded-2xl border border-white/10 p-3 mt-2">
            <MapView stops={stops} height="60vh" />
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {jobs.map((j, i) => {
              const expanded = active === j.job_id;
              const isDone = j.status === "done";
              const isSkipped = j.status === "skipped";
              const isCurrent = j.status === "in_progress";
              const isCompleted = isDone || isSkipped;
              const activeList = jobs.filter(x => x.status !== "done" && x.status !== "skipped");
              const activeIdx = activeList.findIndex(x => x.job_id === j.job_id);
              const canReorder = !isCompleted && activeList.length > 1;
              return (
                <div key={j.job_id} className={`rounded-2xl border transition ${isCompleted ? "bg-white/5 border-white/5 opacity-70" : "bg-white/8 border-white/15"}`} data-testid={`driver-job-${j.job_id}`}>
                  <div className="flex items-center">
                    <button onClick={() => setActive(expanded ? null : j.job_id)} className="flex-1 p-4 text-left flex items-center gap-3" data-testid={`driver-job-${j.job_id}-tap`}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-mono font-bold text-sm flex-shrink-0" style={{ background: isDone ? "rgba(16,185,129,0.4)" : isSkipped ? "rgba(239,68,68,0.4)" : "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }}>
                        {isDone ? <Check size={18} weight="bold" /> : isSkipped ? <X size={18} weight="bold" /> : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{j.customer_name}</div>
                        <div className="text-xs text-white/60 flex items-center gap-1 truncate">
                          <MapPin size={11} weight="bold" /> {j.customer_address?.street}, {j.customer_address?.city}
                        </div>
                      </div>
                      {isCurrent && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--tonneva-secondary)", color: "#0e1a23" }}>Aktiv</span>}
                      {isDone && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-200">Erledigt</span>}
                      {isSkipped && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-red-500/30 text-red-200">Übersprungen</span>}
                    </button>
                    {canReorder && (
                      <div className="flex flex-col gap-0.5 pr-3">
                        <button onClick={(e) => { e.stopPropagation(); moveJob(j.job_id, "up"); }} disabled={activeIdx === 0} className="w-7 h-6 rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center" data-testid={`driver-move-up-${j.job_id}`}>
                          <CaretUp size={12} weight="bold" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); moveJob(j.job_id, "down"); }} disabled={activeIdx === activeList.length - 1} className="w-7 h-6 rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center" data-testid={`driver-move-down-${j.job_id}`}>
                          <CaretDown size={12} weight="bold" />
                        </button>
                      </div>
                    )}
                  </div>
                  {expanded && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/10 mt-1">
                      {isSkipped && (
                        <div className="p-3 rounded-lg text-xs bg-red-500/15 border border-red-500/40" data-testid={`skipped-info-${j.job_id}`}>
                          <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1">Reinigung übersprungen</div>
                          <div className="text-white/95"><strong>Grund:</strong> {j.skip_reason_label || j.skip_reason_code || "—"}</div>
                          {j.skip_notes && <div className="text-white/80 mt-1"><strong>Notiz:</strong> {j.skip_notes}</div>}
                          {j.skip_photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${j.skip_photo_url}`} alt="skip" className="rounded-lg mt-2 max-h-40 object-cover" />}
                          <Button onClick={() => setStatus(j.job_id, "planned")} className="w-full rounded-full mt-3 text-white bg-white/10 hover:bg-white/20" data-testid={`driver-reopen-${j.job_id}`}>
                            Erneut versuchen
                          </Button>
                        </div>
                      )}
                      {j.driver_notes && !isCompleted && (
                        <div className="flex items-start gap-2 p-3 rounded-lg text-xs" style={{ background: "rgba(245, 158, 11, 0.15)", border: "1px solid rgba(245, 158, 11, 0.4)" }} data-testid={`driver-note-${j.job_id}`}>
                          <span className="text-amber-300 font-bold text-sm leading-none">⚠</span>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-0.5">Hinweis vom Backoffice</div>
                            <div className="text-white/95 leading-relaxed">{j.driver_notes}</div>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {j.customer_number && <Info label="Kdnr." value={j.customer_number} />}
                        <Info label="PLZ" value={j.customer_address?.zip} />
                        <Info label="Tonnen" value={j.bins?.length || 0} />
                        {j.customer_phone && <Info label="Telefon" value={j.customer_phone} full />}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {j.customer_phone && (
                          <a href={`tel:${j.customer_phone}`} className="flex-1 min-w-[120px] text-center py-2.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/15 inline-flex items-center justify-center gap-1.5" data-testid={`driver-call-${j.job_id}`}>
                            <Phone size={14} weight="bold" /> Anrufen
                          </a>
                        )}
                        {j.customer_address?.lat && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${j.customer_address.lat},${j.customer_address.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[120px] text-center py-2.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/15 inline-flex items-center justify-center gap-1.5" data-testid={`driver-nav-${j.job_id}`}>
                            <NavigationArrow size={14} weight="bold" /> Navigation
                          </a>
                        )}
                      </div>
                      {j.status === "planned" || j.status === "assigned" ? (
                        <Button onClick={() => setStatus(j.job_id, "in_progress")} className="w-full rounded-full text-white" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid={`driver-start-${j.job_id}`}>
                          <Clock size={14} weight="bold" className="mr-1.5" /> Reinigung starten
                        </Button>
                      ) : null}
                      {j.status === "in_progress" && (
                        <>
                          {(j.bins?.length || 0) > 0 && <BinScanSlot jobId={j.job_id} bins={j.bins || []} scannedBins={j.scanned_bins || []} onScanned={refresh} />}
                          <PhotoSlot label="Vorher" url={j.photo_before} onChange={(f) => uploadPhoto(j.job_id, "before", f)} testId={`photo-before-${j.job_id}`} />
                          <PhotoSlot label="Nachher" url={j.photo_after} onChange={(f) => uploadPhoto(j.job_id, "after", f)} testId={`photo-after-${j.job_id}`} />
                          {j.customer_hub_enabled && <HubConfirmSlot job={j} onDone={refresh} />}
                          <Button onClick={() => setStatus(j.job_id, "done")} className="w-full rounded-full bg-white text-[#0e1a23] hover:bg-white/90 font-bold" data-testid={`driver-complete-${j.job_id}`}>
                            <CheckCircle size={14} weight="bold" className="mr-1.5" /> Als erledigt markieren
                          </Button>
                          <SkipSlot jobId={j.job_id} onDone={refresh} />
                        </>
                      )}
                      {j.status === "done" && (j.photo_before || j.photo_after) && (
                        <div className="grid grid-cols-2 gap-2">
                          {j.photo_before && <img src={`${process.env.REACT_APP_BACKEND_URL}${j.photo_before}`} alt="vorher" className="rounded-lg w-full h-24 object-cover" />}
                          {j.photo_after && <img src={`${process.env.REACT_APP_BACKEND_URL}${j.photo_after}`} alt="nachher" className="rounded-lg w-full h-24 object-cover" />}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {jobs.length > 0 && done === total && (
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-500/40 p-5 text-center" data-testid="tour-complete-panel">
                <Brain size={32} weight="duotone" className="mx-auto mb-2 text-emerald-300" />
                <h3 className="font-heading font-bold text-lg mb-1">Tour komplett!</h3>
                <p className="text-xs text-white/70 mb-4">Speichere die Reihenfolge — beim nächsten Mal wird sie automatisch vorgeschlagen.</p>
                {learnStatus === "saved" ? (
                  <div className="text-sm text-emerald-300 font-bold" data-testid="tour-learn-saved">✓ Reihenfolge gespeichert</div>
                ) : (
                  <button
                    onClick={completeTour}
                    disabled={learnStatus === "saving"}
                    className="px-6 py-3 rounded-full text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                    data-testid="tour-learn-btn"
                  >
                    {learnStatus === "saving" ? "Speichere…" : "Reihenfolge merken"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Bottom safe-area */}
      <div className="h-8" />
    </div>
  );
}
